import axios from 'axios';

const API_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:5000/api'
    : 'https://consultancy-cms-0l6p.onrender.com/api');

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000, // Reduced from 30s to 15s for better UX
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Fast token injection
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    // SECURITY: Validate token format before attaching (basic sanity check)
    if (
      token &&
      token !== 'null' &&
      token !== 'undefined' &&
      token.length > 10 &&
      token.length < 1000 &&
      token.split('.').length === 3
    ) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (token) {
      // SECURITY: Clear corrupted/invalid tokens from storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Global error handling without blocking
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 1. Silent handling of rate limits
    if (error.response?.status === 429) {
      console.warn('Rate limited:', error.config?.url);
      return Promise.reject(error);
    }

    // 2. Auth error handling
    if (error.response?.status === 401) {
      const publicPaths = ['/track-application', '/apply', '/contact', '/book-appointment'];
      const isPublicPage = publicPaths.some((p) => window.location.pathname.startsWith(p));
      const isLoginPage = window.location.pathname === '/login';

      if (!isPublicPage && !isLoginPage && window.location.pathname.startsWith('/admin')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Exporting API Objects
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  changePassword: (data) => api.put('/auth/password', data),
};

export const universityAPI = {
  getAll: (params) => api.get('/universities', { params }),
  getFeatured: () => api.get('/universities/featured'),
  getByCountry: (country) => api.get(`/universities/country/${encodeURIComponent(country)}`),
  getBySlug: (slug) => api.get(`/universities/slug/${encodeURIComponent(slug)}`),
  getById: (id) => api.get(`/universities/${encodeURIComponent(id)}`),
  create: (data) => api.post('/universities', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/universities/${encodeURIComponent(id)}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/universities/${encodeURIComponent(id)}`),
};

export const applicationAPI = {
  create: (data) => api.post('/applications', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getAll: (params) => api.get('/applications', { params }),
  getById: (id) => api.get(`/applications/${encodeURIComponent(id)}`),
  update: (id, data) => api.put(`/applications/${encodeURIComponent(id)}`, data),
  addNote: (id, note) => api.post(`/applications/${encodeURIComponent(id)}/notes`, { note }),
  delete: (id) => api.delete(`/applications/${encodeURIComponent(id)}`),
  getStats: () => api.get('/applications/stats'),
  getByApplicationId: (appId) => api.get(`/applications/track/${encodeURIComponent(appId)}`),
  exportCSV: () => api.get('/applications/export/csv', { responseType: 'blob' }),
};

export const appointmentAPI = {
  create: (data) => api.post('/appointments', data),
  getAll: (params) => api.get('/appointments', { params }),
  getById: (id) => api.get(`/appointments/${encodeURIComponent(id)}`),
  exportCSV: () => api.get('/appointments/export/csv', { responseType: 'blob' }),
  update: (id, data) => api.put(`/appointments/${encodeURIComponent(id)}`, data),
  delete: (id) => api.delete(`/appointments/${encodeURIComponent(id)}`),
  getAvailableSlots: (date) => api.get(`/appointments/slots/${encodeURIComponent(date)}`),
  getToday: () => api.get('/appointments/today'),
};

export const blogAPI = {
  getAll: (params) => api.get('/blogs', { params }),
  getBySlug: (slug) => api.get(`/blogs/${encodeURIComponent(slug)}`),
  getAllAdmin: () => api.get('/blogs/admin/all'),
  create: (data) => api.post('/blogs', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/blogs/${encodeURIComponent(id)}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/blogs/${encodeURIComponent(id)}`),
};

export const testimonialAPI = {
  getAll: (params) => api.get('/testimonials', { params }),
  create: (data) => api.post('/testimonials', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/testimonials/${encodeURIComponent(id)}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/testimonials/${encodeURIComponent(id)}`),
};

export const contactAPI = {
  create: (data) => api.post('/contacts', data),
  getAll: (params) => api.get('/contacts', { params }),
  reply: (id, data) => api.put(`/contacts/${encodeURIComponent(id)}/reply`, data),
  update: (id, data) => api.put(`/contacts/${encodeURIComponent(id)}`, data),
  delete: (id) => api.delete(`/contacts/${encodeURIComponent(id)}`),
  exportCSV: () => api.get('/contacts/export/csv', { responseType: 'blob' }),
};

export const galleryAPI = {
  getAll: (params) => api.get('/gallery', { params }),
  getAllAdmin: () => api.get('/gallery/admin/all'),
  create: (data) => api.post('/gallery', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/gallery/${encodeURIComponent(id)}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/gallery/${encodeURIComponent(id)}`),
};

export const scholarshipAPI = {
  getAll: (params) => api.get('/scholarships', { params }),
  getAllAdmin: () => api.get('/scholarships/admin/all'),
  getBySlug: (slug) => api.get(`/scholarships/${encodeURIComponent(slug)}`),
  create: (data) => api.post('/scholarships', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/scholarships/${encodeURIComponent(id)}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/scholarships/${encodeURIComponent(id)}`),
};

export const eventAPI = {
  getAll: (params) => api.get('/events', { params }),
  getAllAdmin: () => api.get('/events/admin/all'),
  getBySlug: (slug) => api.get(`/events/${encodeURIComponent(slug)}`),
  create: (data) => api.post('/events', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/events/${encodeURIComponent(id)}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/events/${encodeURIComponent(id)}`),
};

export const courseAPI = {
  getAll: (params) => api.get('/courses', { params }),
  getBySlug: (slug) => api.get(`/courses/${encodeURIComponent(slug)}`),
  getById: (id) => api.get(`/courses/id/${encodeURIComponent(id)}`),
  getByCategory: (category) => api.get(`/courses/category/${encodeURIComponent(category)}`),
  getAllAdmin: () => api.get('/courses/admin/all'),
  create: (data) => api.post('/courses', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/courses/${encodeURIComponent(id)}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/courses/${encodeURIComponent(id)}`),
};

export const studyAbroadAPI = {
  getAll: () => api.get('/study-abroad'),
  getAllAdmin: () => api.get('/study-abroad/admin/all'),
  getBySlug: (slug) => api.get(`/study-abroad/slug/${encodeURIComponent(slug)}`),
  getById: (id) => api.get(`/study-abroad/${encodeURIComponent(id)}`),
  create: (data) => api.post('/study-abroad', data),
  update: (id, data) => api.put(`/study-abroad/${encodeURIComponent(id)}`, data),
  delete: (id) => api.delete(`/study-abroad/${encodeURIComponent(id)}`),
};

export const settingsAPI = {
  getAll: () => api.get('/settings'),
  getSection: (section) => api.get(`/settings/${encodeURIComponent(section)}`),
  updateGeneral: (data) => api.put('/settings/general', data),
  updateSocialMedia: (data) => api.put('/settings/social-media', data),
  updateHomePage: (data) => api.put('/settings/home-page', data),
  updateHomeSection: (section, data) => api.put(`/settings/home-page/${encodeURIComponent(section)}`, data),
  updateAboutPage: (data) => api.put('/settings/about-page', data),
  updateHeader: (data) => api.put('/settings/header', data),
  updateFooter: (data) => api.put('/settings/footer', data),
  updateSEO: (data) => api.put('/settings/seo', data),
  updatePopup: (data) => api.put('/settings/popup', data),
  updateSection: (section, data) => api.put(`/settings/${encodeURIComponent(section)}`, data),
  updateWhatsApp: (data) => api.put('/settings/whatsapp-button', data),
  uploadImage: (formData) => api.post('/settings/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  reset: () => api.post('/settings/reset'),
};

export default api;
